import React, { useState, useMemo } from 'react';
import { useClientPOs } from '@/hooks/useClientPOs';
import { useSession } from '@/lib/SessionContext';
import { Search, FileText, CheckCircle2, ChevronRight, Plus, Eye, Check, Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { format } from 'date-fns';
import { id } from 'date-fns/locale';
import SphPrintModal from '@/components/marketing/SphPrintModal';
import { useToast } from '@/components/ui/use-toast';
import { useNavigate } from 'react-router-dom';
import { base44, supabase } from '@/api/base44Client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAsaPersonality } from '@/hooks/useAsaPersonality';
import { useBatchSelection } from '@/hooks/useBatchSelection';
import BatchActionBar from '@/components/ui/BatchActionBar';
import { Checkbox } from '@/components/ui/checkbox';
import { useBankAccounts } from '@/hooks/useBankAccounts';

const MarketingSPHList = () => {
  const { member } = useSession();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { accountsData, accountsMap } = useBankAccounts();
  const { clientPOs, isLoading, refetch, updatePO, deletePO } = useClientPOs();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedSph, setSelectedSph] = useState(null);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [accConfirmData, setAccConfirmData] = useState(null);

  const queryClient = useQueryClient();
  const { sapaan, isFadli } = useAsaPersonality();
  const { data: clientAccounts = [] } = useQuery({ queryKey: ['client-accounts'], queryFn: () => base44.entities.ClientAccount.list() });

  // Filter ONLY SPH
  const sphList = useMemo(() => {
    return clientPOs.filter(po => po.poMode === 'sph');
  }, [clientPOs]);

  const filteredInvoices = useMemo(() => {
    return sphList.filter(inv => {
      const matchSearch = inv.id.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          (inv.customerName || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchStatus = statusFilter === 'all' ? true : inv.statusBayar === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [sphList, searchTerm, statusFilter]);

  const {
    selectedIds,
    toggleSelection,
    selectAll,
    clearSelection,
    isSelectionMode,
    selectedCount,
    totalCount
  } = useBatchSelection(filteredInvoices);

  const handleBatchDelete = async () => {
    if (!window.confirm(`Yakin ingin menghapus ${selectedCount} SPH terpilih?`)) return;
    try {
      const idsArray = Array.from(selectedIds);
      await Promise.all(idsArray.map(id => deletePO(id)));
      toast({
        title: "Berhasil",
        description: `${selectedCount} SPH berhasil dihapus.`,
      });
      clearSelection();
      refetch();
    } catch (err) {
      toast({
        title: "Gagal",
        description: "Gagal menghapus SPH: " + err.message,
        variant: "destructive"
      });
    }
  };

  const handleAccClick = (sph) => {
    const brandName = (sph.customerName || '').split(' / ')[0];
    const client = clientAccounts.find(c => c.brand_name === brandName);
    const phone = sph.phone || '-';
    const address = sph.customerAddress || '-';
    const hasAddress = (phone !== '-' && phone !== '') || (address !== '-' && address !== '');

    let isNewClient = false;
    let newAddress = null;

    if (hasAddress) {
      if (!client) {
         isNewClient = true;
         newAddress = { nama_alamat: '', alamat_lengkap: address, no_hp: phone };
      } else {
         const addressExists = client.addresses?.some(a => a.alamat_lengkap === address);
         if (!addressExists) {
            newAddress = { nama_alamat: '', alamat_lengkap: address, no_hp: phone };
         }
      }
    }

    setAccConfirmData({
       sph,
       isNewClient,
       clientData: client,
       newAddress,
       saveAddress: !!newAddress,
       termin: 'cash',
       jatuh_tempo: '',
       deadline_produksi: '',
       bank: 'BCA'
    });
  };

  const processAcc = async ({ sph, isNewClient, clientData, newAddress, saveAddress }) => {
    try {
      const brandName = (sph.customerName || '').split(' / ')[0].trim();
      const clientPic = (sph.customerName || '').split(' / ')[1]?.trim() || sph.pic || '';
      const marketingPic = member?.full_name || member?.nama || member?.name || 'Staff Marketing';

      if (saveAddress && newAddress) {
        const fullAddrObj = {
          id: `addr-${Date.now()}`,
          nama_alamat: newAddress.nama_alamat || 'Kantor',
          penerima: newAddress.penerima || '',
          telepon: newAddress.no_hp || newAddress.telepon || sph.phone || '',
          alamat_lengkap: newAddress.alamat_lengkap || sph.customerAddress || '',
          kode_pos: newAddress.kode_pos || ''
        };

        if (isNewClient || !clientData) {
          await base44.entities.ClientAccount.create({
            brand_name: brandName,
            company_name: brandName,
            owner_name: clientPic,
            pic: marketingPic,
            pic_name: marketingPic,
            phone: newAddress.no_hp || sph.phone || '',
            status: 'verified',
            addresses: [fullAddrObj]
          });
        } else if (clientData) {
          const updatedAddresses = [...(clientData.addresses || []), fullAddrObj];
          await base44.entities.ClientAccount.update(clientData.id, { 
            addresses: updatedAddresses,
            status: 'verified'
          });
        }
        queryClient.invalidateQueries({ queryKey: ['client-accounts'] });
        queryClient.invalidateQueries({ queryKey: ['clients'] });
        toast({ title: 'Tersimpan', description: 'Alamat berhasil disimpan ke Data Klien.' });
      } else if (isNewClient && brandName) {
        // Otomatis daftarkan brand ke ClientAccount jika brand baru
        await base44.entities.ClientAccount.create({
          brand_name: brandName,
          company_name: brandName,
          owner_name: clientPic,
          pic: marketingPic,
          pic_name: marketingPic,
          phone: sph.phone || '',
          status: 'verified',
          addresses: []
        });
        queryClient.invalidateQueries({ queryKey: ['client-accounts'] });
        queryClient.invalidateQueries({ queryKey: ['clients'] });
      }

      // Generate format nomor invoice INV-[BRAND]-[ROMAN_MONTH]-[COUNT]
      const cleanBrand = String(brandName || 'BRAND').trim().replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
      const mDate = new Date();
      const romanMonths = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII'];
      const monthChar = romanMonths[mDate.getMonth()];

      let count = 1;
      let generatedInvId = `INV-${cleanBrand}-${monthChar}-${count.toString().padStart(3, '0')}`;
      
      // Hitung nomor urut berikutnya yang belum terpakai di bulan ini
      while (clientPOs.some(po => po.id === generatedInvId && po.id !== sph.id)) {
        count++;
        generatedInvId = `INV-${cleanBrand}-${monthChar}-${count.toString().padStart(3, '0')}`;
      }

      await updatePO(sph.id, {
        id: generatedInvId,
        poMode: 'custom', 
        isSPH: false,
        paymentTerms: accConfirmData?.termin || 'cash',
        is_tempo: accConfirmData?.termin === 'tempo',
        statusBayar: accConfirmData?.termin === 'tempo' ? 'TEMPO' : 'BELUM DIBAYAR',
        statusProduksi: 'BELUM NAIK PRODUKSI',
        date: format(new Date(), 'dd MMM yyyy'),
        created_date: new Date().toISOString(),
        acc_date: new Date().toISOString(),
        deadline: accConfirmData?.deadline_produksi || null,
        tanggal_jatuh_tempo: accConfirmData?.termin === 'tempo' ? (accConfirmData?.jatuh_tempo || null) : null,
        bank_tujuan: accConfirmData?.bank || 'BCA',
        acc_by: member?.full_name || member?.name || 'Sistem'
      });

      queryClient.invalidateQueries({ queryKey: ['client-pos'] });
      queryClient.invalidateQueries({ queryKey: ['client-accounts'] });
      queryClient.invalidateQueries({ queryKey: ['clients'] });

      toast({
        title: "Berhasil",
        description: "SPH telah disetujui dan diubah menjadi Invoice.",
      });
      setAccConfirmData(null);
      refetch();
    } catch (err) {
      toast({
        title: "Gagal",
        description: "Gagal meng-ACC SPH: " + err.message,
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (sph) => {
    if (!window.confirm(`Yakin ingin menghapus SPH ${sph.id}?`)) return;
    try {
      await deletePO(sph.id);
      toast({
        title: "Berhasil",
        description: "SPH berhasil dihapus.",
      });
      refetch();
    } catch (err) {
      toast({
        title: "Gagal",
        description: "Gagal menghapus SPH: " + err.message,
        variant: "destructive"
      });
    }
  };

  const fmt = (num) => Number(num || 0).toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const calculateTotal = (sph) => {
    const rawLegalitas = typeof sph.legalitas === 'string' ? JSON.parse(sph.legalitas) : (sph.legalitas || []);
    const itemsTotal = sph.items?.reduce((s, i) => s + (i.hargaTotal || 0), 0) || 0;
    const legalitasTotal = (rawLegalitas || []).reduce((s, l) => s + ((parseFloat(l.harga_jual) || 0) * (l.qty_variant || 1)), 0);
    return itemsTotal + legalitasTotal;
  };

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto space-y-6 animate-in fade-in zoom-in-95 relative">
      <BatchActionBar 
        show={isSelectionMode && selectedCount > 0} 
        selectedCount={selectedCount} 
        onSelectAll={selectAll} 
        onDeselectAll={clearSelection} 
        isAllSelected={selectedCount === totalCount && totalCount > 0}
      >
        <Button size="sm" variant="destructive" onClick={handleBatchDelete} className="gap-2">
          <Trash2 className="w-4 h-4" /> Hapus Data
        </Button>
      </BatchActionBar>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-black text-[#302621] tracking-tight">Daftar SPH</h1>
          <p className="text-sm text-[#7A695B] mt-1">Kelola dan setujui Surat Penawaran Harga (SPH)</p>
        </div>
        <Button onClick={() => navigate('/office/marketing/calculator')} className="bg-[#8C6208] hover:bg-[#6e4c06] text-white">
          <Plus className="w-4 h-4 mr-2" /> Buat SPH Baru
        </Button>
      </div>

      <div className="flex flex-col md:flex-row gap-4 mb-6 bg-white p-4 rounded-xl shadow-sm border border-[#E6D5B8]">
        <div className="flex-1 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input 
            placeholder="Cari Nomor SPH atau Brand..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 bg-slate-50 border-slate-200"
          />
        </div>
        <div className="w-full md:w-48">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="bg-slate-50 border-slate-200">
              <SelectValue placeholder="Semua Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Status</SelectItem>
              <SelectItem value="BELUM DIBAYAR">Menunggu ACC</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="w-8 h-8 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#7A695B] font-medium">Memuat data SPH...</p>
        </div>
      ) : filteredInvoices.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-[#E6D5B8] p-12 text-center">
          <div className="w-16 h-16 bg-[#F9F5EC] rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-[#A39171]" />
          </div>
          <h3 className="text-lg font-bold text-[#302621] mb-2">Belum Ada SPH</h3>
          <p className="text-[#7A695B]">Data Surat Penawaran Harga tidak ditemukan atau belum ada yang dibuat.</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredInvoices.map((sph) => {
            const totalTagihan = calculateTotal(sph) + (sph.shippingCost || 0) + (sph.ppn_percentage > 0 ? (calculateTotal(sph) * sph.ppn_percentage / 100) : 0);
            return (
            <div key={sph.id} className={`bg-white rounded-xl border p-4 flex flex-col md:flex-row items-center justify-between gap-4 shadow-sm transition-all ${selectedIds.has(sph.id) ? 'border-amber-400 bg-amber-50/30' : 'border-[#E6D5B8] hover:border-[#D4AF37]'}`}>
              <div className="flex items-center gap-4 w-full md:w-auto">
                {isSelectionMode && (
                  <Checkbox 
                    checked={selectedIds.has(sph.id)}
                    onCheckedChange={() => toggleSelection(sph.id)}
                    className="w-5 h-5 rounded border-slate-300 data-[state=checked]:bg-amber-600 data-[state=checked]:border-amber-600"
                  />
                )}
                <div className="w-12 h-12 rounded-full bg-[#F9F5EC] flex items-center justify-center shrink-0">
                  <FileText className="w-6 h-6 text-[#8C6208]" />
                </div>
                <div>
                  <h4 className="font-bold text-[#302621] text-lg">{sph.id}</h4>
                  <p className="text-sm font-semibold text-[#8C6208]">{sph.customerName}</p>
                  <p className="text-xs text-[#7A695B] mt-0.5">{sph.date || ''} • PIC: {sph.pic || '-'}</p>
                </div>
              </div>
              <div className="w-full md:w-auto text-left md:text-right flex-1">
                <p className="text-[11px] text-slate-400 font-bold uppercase tracking-wider mb-1">Total Penawaran</p>
                <p className="text-xl font-black text-emerald-600">Rp {fmt(totalTagihan)}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2 w-full md:w-auto shrink-0 mt-3 md:mt-0">
                <Button variant="outline" size="sm" onClick={() => navigate(`/office/marketing/calculator?edit=${sph.id}`)} className="flex-1 md:flex-none border-amber-500 text-amber-600 hover:bg-amber-50" title="Edit SPH">
                  <Pencil className="w-4 h-4 md:mr-2" /> <span className="hidden md:inline">Edit</span>
                </Button>
                <Button variant="outline" size="sm" onClick={() => startSelection(sph.id)} className="flex-1 md:flex-none border-red-500 text-red-600 hover:bg-red-50" title="Pilih untuk Dihapus">
                  <Trash2 className="w-4 h-4 md:mr-2" /> <span className="hidden md:inline">Hapus</span>
                </Button>
                <Button variant="outline" size="sm" onClick={() => { setSelectedSph(sph); setIsPrintModalOpen(true); }} className="flex-1 md:flex-none border-[#D4AF37] text-[#8C6208] hover:bg-[#F9F5EC]" title="Cetak SPH">
                  <Eye className="w-4 h-4 md:mr-2" /> <span className="hidden md:inline">Cetak</span>
                </Button>
                <Button size="sm" onClick={() => handleAccClick(sph)} className="flex-1 md:flex-none bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-600/20" title="ACC Penawaran">
                  <Check className="w-4 h-4 md:mr-2" /> <span className="hidden md:inline">ACC</span>
                </Button>
              </div>
            </div>
          )})}
        </div>
      )}

      {isPrintModalOpen && selectedSph && (
        <SphPrintModal 
          isOpen={isPrintModalOpen} 
          onClose={() => { setIsPrintModalOpen(false); setSelectedSph(null); }} 
          sphData={selectedSph} 
        />
      )}

      {/* Asa Confirm Modal */}
      {accConfirmData && (
        <div className="fixed inset-0 z-[1000] bg-black/60  flex items-center justify-center p-4 animate-in fade-in">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full text-center shadow-xl border border-[#E6D5B8]">
             <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-amber-300 shadow-md mx-auto mb-4 bg-amber-50 shrink-0">
               <img src="/asa-profile.png" alt="ASA" className="w-full h-full object-cover" onError={(e) => { e.target.src = 'https://ui-avatars.com/api/?name=Asa&background=0D8ABC&color=fff'; }} />
             </div>
             <h3 className="text-lg font-bold text-[#302621] mb-2">Pesan dari Asa</h3>
             <p className="text-sm text-slate-600 mb-6 text-left leading-relaxed">
               {accConfirmData.newAddress ? (
                 isFadli 
                   ? `Fadli, SPH untuk ${accConfirmData.sph.customerName?.split(' / ')[0]} udah deal nih ya? Asa liat ini alamatnya ${accConfirmData.isNewClient ? 'baru banget' : 'beda dari yang disimpen'}. Mau Asa simpen sekalian nggak ke Data Klien biar besok-besok gampang?` 
                   : `Halo ${sapaan}, Anda akan menyetujui SPH untuk ${accConfirmData.sph.customerName?.split(' / ')[0]}. Kami mendeteksi alamat baru, apakah ingin disimpan ke Data Klien?`
               ) : (
                 isFadli
                   ? `Fadli, yakin mau ACC SPH untuk ${accConfirmData.sph.customerName?.split(' / ')[0]} ini jadi Invoice? Udah fix semua kan harganya?`
                   : `Halo ${sapaan}, Anda yakin ingin menyetujui SPH ini menjadi Invoice?`
               )}
             </p>
             
             {accConfirmData.newAddress && (
               <div className="text-left mb-6">
                 <label className="flex items-center gap-3 text-sm text-[#302621] font-bold cursor-pointer bg-[#F9F5EC] p-3 rounded-lg border border-[#E6D5B8] hover:bg-[#f3ead1] transition-colors">
                   <input 
                     type="checkbox" 
                     checked={accConfirmData.saveAddress} 
                     onChange={e => setAccConfirmData({...accConfirmData, saveAddress: e.target.checked})} 
                     className="w-5 h-5 rounded text-[#8C6208] focus:ring-[#8C6208] border-slate-300" 
                   />
                   Ya, simpan alamat ke Data Klien
                 </label>
                 
                 {accConfirmData.saveAddress && (
                   <div className="mt-4 animate-in slide-in-from-top-2 fade-in">
                     <label className="text-[10px] font-bold text-[#7A695B] uppercase tracking-wider block mb-1">Kasih Nama Alamat (Contoh: Rumah / Kantor)</label>
                     <Input 
                       placeholder="Nama Alamat..." 
                       value={accConfirmData.newAddress.nama_alamat} 
                       onChange={e => setAccConfirmData({...accConfirmData, newAddress: {...accConfirmData.newAddress, nama_alamat: e.target.value}})} 
                       className="h-10 text-sm border-slate-200 focus:border-[#8C6208] focus:ring-[#8C6208]"
                     />
                   </div>
                 )}
               </div>
             )}

             <div className="text-left mb-6 mt-4 space-y-4">
               <div>
                 <label className="text-[10px] font-bold text-[#7A695B] uppercase tracking-wider block mb-1.5">Termin Pembayaran</label>
                 <div className="grid grid-cols-2 gap-2 bg-slate-100 p-1 rounded-xl border border-slate-200/60">
                   <button
                     type="button"
                     onClick={() => setAccConfirmData({ ...accConfirmData, termin: 'cash' })}
                     className={`h-9 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-1.5 ${accConfirmData.termin === 'cash' ? 'bg-white text-[#8C6208] shadow-xs border border-[#E6D5B8]' : 'text-slate-500 hover:text-slate-800'}`}
                   >
                     <span>Cash / Lunas</span>
                   </button>
                   <button
                     type="button"
                     onClick={() => setAccConfirmData({ ...accConfirmData, termin: 'tempo' })}
                     className={`h-9 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-1.5 ${accConfirmData.termin === 'tempo' ? 'bg-white text-[#8C6208] shadow-xs border border-[#E6D5B8]' : 'text-slate-500 hover:text-slate-800'}`}
                   >
                     <span>Tempo</span>
                   </button>
                 </div>
               </div>
               
               {accConfirmData.termin === 'tempo' && (
                 <div className="animate-in slide-in-from-top-2 fade-in">
                   <label className="text-[10px] font-bold text-[#7A695B] uppercase tracking-wider block mb-1">Tanggal Jatuh Tempo</label>
                   <Input 
                     type="date"
                     value={accConfirmData.jatuh_tempo} 
                     onChange={e => setAccConfirmData({...accConfirmData, jatuh_tempo: e.target.value})} 
                     className="h-10 text-sm border-slate-200 focus:border-[#8C6208] focus:ring-[#8C6208]"
                   />
                 </div>
               )}

               <div>
                 <label className="text-[10px] font-bold text-[#7A695B] uppercase tracking-wider block mb-1">Deadline Produksi</label>
                 <Input 
                   type="date"
                   value={accConfirmData.deadline_produksi} 
                   onChange={e => setAccConfirmData({...accConfirmData, deadline_produksi: e.target.value})} 
                   className="h-10 text-sm border-slate-200 focus:border-[#8C6208] focus:ring-[#8C6208]"
                 />
               </div>

                <div>
                  <label className="text-[10px] font-bold text-[#7A695B] uppercase tracking-wider block mb-1.5">Bank Tujuan Invoice</label>
                  <div className="grid grid-cols-3 gap-2 bg-slate-100 p-1 rounded-xl border border-slate-200/60">
                    {(accountsData && accountsData.length > 0 ? accountsData : [
                      { nama_bank: 'bca', name: 'BCA Satala' },
                      { nama_bank: 'mandiri', name: 'Mandiri Satala' },
                      { nama_bank: 'bni', name: 'BNI Satala' }
                    ]).map((acc) => {
                      const bankTitle = acc.name || (acc.nama_bank || 'BCA').toUpperCase();
                      const isSelected = (accConfirmData.bank || 'BCA').toLowerCase() === (acc.nama_bank || acc.name || '').toLowerCase() ||
                                         (accConfirmData.bank || 'BCA').toLowerCase().includes((acc.nama_bank || '').toLowerCase());
                      return (
                        <button
                          key={acc.nama_bank || acc.name}
                          type="button"
                          onClick={() => setAccConfirmData({ ...accConfirmData, bank: acc.name || acc.nama_bank || 'BCA' })}
                          className={`h-9 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-1 ${
                            isSelected
                              ? 'bg-white text-[#8C6208] shadow-xs border border-[#E6D5B8]'
                              : 'text-slate-500 hover:text-slate-800'
                          }`}
                        >
                          {bankTitle}
                        </button>
                      );
                    })}
                  </div>
                </div>
             </div>

             <div className="flex gap-3 mt-2">
               <Button variant="outline" className="flex-1 border-slate-200 text-slate-600 hover:bg-slate-50" onClick={() => setAccConfirmData(null)}>Batal</Button>
               <Button 
                 className="flex-1 bg-[#8C6208] text-white hover:bg-[#6c4b05] shadow-lg shadow-amber-900/20" 
                 disabled={accConfirmData.newAddress && accConfirmData.saveAddress && !accConfirmData.newAddress.nama_alamat?.trim()}
                 onClick={() => processAcc(accConfirmData)}
               >
                 {isFadli ? 'Gas, ACC!' : 'Ya, Setujui SPH'}
               </Button>
             </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketingSPHList;
